import { useState, useEffect, useRef, type ReactNode } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { contractsApi, CUSTOMER_SEGMENT_OPTIONS, type ContractCreateRequest, type CustomerDocumentCandidate } from '../api/contracts'
import { customersApi, type Customer, type CustomerDetail } from '../api/customers'
import { newIdempotencyKey, ApiError } from '../api/client'
import { unoMobileApi, UNO_MOBILE_OPERATOR_IDS, type UnoMobileReferrer } from '../api/unomobile'
import { fetchMe, type LoginResponse } from '../api/auth'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import { fetchDepartments, fetchOperators, fetchProducts, type Department, type Operator, type Product } from '../api/catalog'
import { todayInRome } from '../lib/productSelection'
import { addressFields, applyClientAddress, clientAddressFromDocs, portalFromDoc } from '../lib/docPortal'
import { SIGNED_PDA_DOCUMENT_TYPE, SIGNED_PDA_KEY, operatorPortalConfig, tipoClienteFromSegment } from '../lib/operatorPortalConfig'
import { PROFILE_SKY, skyTipologiaDaProdotto } from '../lib/portalProfiles'
import { FileTile } from '../components/FileTile'
import { ContractDynamicFields } from '../components/ContractDynamicFields'
import { contractFieldsApi, type FieldValue } from '../api/dynamicFields'
import { useFeatureFlag } from '../lib/featureFlags'
import { isMasked } from '../api/sensitive'
import { RevealSensitiveButton } from '../components/RevealSensitiveButton'
import {
  DRAFT_INTERVAL_MS, DRAFT_KEY_PREFIX, readDraft as readStoredDraft, removeDraft, shouldWriteDraft, writeDraft,
  type StoredDraft as StoredDraftOf,
} from '../lib/contractDraft'
import { documentsApi } from '../api/documents'
import { DOC_SLOTS, distinctUploadFile, docSlotsFor, mergeReads, visibleFields, type DocSlotDef, type DocSlotKey } from '../lib/docSlots'
import { PraticaPortalSections, missingPortalFields, withPortalDefaults } from '../components/PraticaPortalSections'
import { FornitureNoteSelect } from '../components/FornitureNoteSelect'
import { supplyPointsApi } from '../api/customers'
import { customerFromDocs, docIdentity, legalRepFromDocs } from '../lib/customerMatch'
import { createOrLinkCustomer } from '../lib/customerLink'
import { LINKED_PRACTICES_KEY, simErrors, simPortalData, type SimLine } from '../lib/simLines'
import { PraticaSimSection } from '../components/PraticaSimSection'
import { FormGrid, Button, ButtonLink, Callout, Card, HelpTip, Icon, isExplanation, List, ListItem, Page, PageHeader, Select, TextField } from '../components/ui'
import '../styles/contract-page.css'

/**
 * F-141 (REQ-041-001): salvataggio automatico del modulo "Nuova pratica", sotto il flag
 * `contratti.salvataggio_automatico`. In `localStorage` (decisione S4 del 2026-10-11: la bozza sopravvive alla chiusura della scheda; si cancella al logout), una chiave per utente, nessun file:
 * restano i campi del modulo, non gli allegati ne' i campi aggiuntivi. Ogni accesso e' protetto: se lo storage non c'e'
 * il modulo funziona come prima.
 */
function clockLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' })
}

// REQ-006-003: same rendering rule as CustomersPage.tsx's own (unexported) `displayName` — a
// company customer shows its ragione sociale, everyone else their first+last name, falling back
// to the customerCode when neither is set. Duplicated here rather than imported, same call as
// `isEqualStaff` above: this is a small page-local helper, not a shared component, and
// `CustomersPage.tsx` doesn't export it.
function displayName(c: Customer): string {
  if (c.customerType === 'company') return c.companyName ?? c.customerCode
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || c.customerCode
}

/** Indirizzo su una riga -> campi portale separati (via, civico, comune, provincia, CAP). */
/** Ricompone l'indirizzo completo di un punto di fornitura dai campi del portale. */
function fullAddress(d: Record<string, string>, s: 'POD' | 'PDR'): string | undefined {
  const street = [d[`INDIRIZZO_${s}`], d[`NUM_CIVICO_${s}`]].filter(Boolean).join(' ')
  const prov = d[`PROVINCIA_${s}`] ? `(${d[`PROVINCIA_${s}`]})` : ''
  const place = [d[`CAP_${s}`], d[`ID_COMUNE_${s}`], prov].filter(Boolean).join(' ')
  return [street, place].filter(Boolean).join(', ') || undefined
}

/** Dall'anagrafica del cliente scelto ai campi del portale (si riempiono solo quelli ancora vuoti). */
function portalFromCustomer(c: Customer | CustomerDetail): { map: Record<string, string>; segment: 'consumer' | 'business' } {
  const business = c.customerType === 'company' || c.customerType === 'condominium'
  const m: Record<string, string | undefined> = business
    ? { RAGIONE_SOCIALE_CLIENTE: c.companyName }
    : { NOME_CLIENTE: c.firstName, COGNOME_CLIENTE: c.lastName }
  Object.assign(m, {
    CODICE_FISCALE_CLIENTE: c.fiscalCode, PARTITA_IVA_CLIENTE: c.vatNumber,
    TEL_CLIENTE: c.mobile || c.phone, CELLULARE_CLIENTE: c.mobile, EMAIL_SPED: c.email, EMAIL_PEC_CLIENTE: c.pec,
  }, addressFields('CLIENTE', c.address))
  // Comune, CAP e provincia dell'anagrafica prevalgono su quelli ricavati dalla riga dell'indirizzo.
  if (c.postalCode?.trim()) m['CAP_CLIENTE'] = c.postalCode
  if (c.city?.trim()) m['ID_COMUNE_CLIENTE'] = c.city
  if (c.province?.trim()) m['PROVINCIA_CLIENTE'] = c.province
  const map: Record<string, string> = {}
  // Mai un valore mascherato (C-08) nel modulo: finirebbe salvato con gli asterischi.
  for (const [k, v] of Object.entries(m)) if (v?.trim() && !isMasked(v)) map[k] = v.trim()
  return { map, segment: business || c.vatNumber?.trim() ? 'business' : 'consumer' }
}

// Documenti letti all'inizio della pratica (riquadri in lib/docSlots): i campi si rileggono a mano, riempiono i dati del
// portale ancora vuoti e il file diventa allegato della pratica alla creazione (tipo documento = tipo di lettura).
interface DocSlot {
  file: File | null
  fields: Record<string, string>
  /** Esito della lettura: chi ha letto (IA o modello del fornitore) e i valori letti, prima delle correzioni. */
  read: { source?: string | undefined; values: Record<string, string> } | null
  busy: boolean
  error: string | null
}
const EMPTY_SLOT: DocSlot = { file: null, fields: {}, read: null, busy: false, error: null }

interface FormState {
  contractType: string
  // REQ-006-005: wizard "Nuova pratica" step 3 ("Select Sector"). '' means "not yet classified"
  // and is never sent — same guarded-field convention as `customerSegment` right below (kept a
  // plain string, not `number | ''`, so the shared `setField` helper stays usable for it too).
  departmentId: string
  agentId: string
  // REQ-006-004: '' means "not yet classified" (no radio selected) and is never sent to the
  // server — same "plain string, mapped/validated at submit time" convention as
  // CustomerFormPage.tsx's own `customerType` (kept a plain string rather than a literal union so
  // the generic `setField` helper below stays usable for every text-like field, this one included).
  customerSegment: string
  // Operatore con cui si carica la pratica (scelto per primo): filtra i prodotti e decide il portale di caricamento.
  operatorId: string
  // F-055 (TASK-0309, REQ-006-006): '' = none chosen yet; required by the UI (the API keeps it optional).
  productId: string
  baseAmount: string
  addonTotal: string
  // Subito dopo l'operatore si dice se il cliente esiste già ('existing': si sceglie dall'anagrafica) o è nuovo
  // ('new': si crea al salvataggio dai documenti e dai dati cliente della pratica). '' = non ancora detto.
  customerMode: string
  customerId: string
  // Data di firma (yyyy-MM-dd), di default oggi.
  signedAt: string
  notes: string
  // Dati richiesti dal portale dell'operatore (JSON chiave->valore): stringa perche' la bozza tiene solo stringhe.
  portalJson: string
  // SIM collegate (Fastweb), JSON di SimLine[]: ognuna diventa una pratica collegata alla principale.
  simsJson: string
}

const EMPTY_FORM: FormState = {
  contractType: '',
  departmentId: '',
  agentId: '',
  customerSegment: '',
  operatorId: '',
  productId: '',
  baseAmount: '',
  addonTotal: '0',
  customerMode: '',
  customerId: '',
  signedAt: '',
  notes: '',
  portalJson: '{}',
  simsJson: '[]',
}

type StoredDraft = StoredDraftOf<FormState>

function readDraft(key: string): StoredDraft | null {
  // Le bozze salvate prima della data di firma la ricevono vuota: si propone oggi.
  const d = readStoredDraft(key, EMPTY_FORM)
  return d && !d.form.signedAt ? { ...d, form: { ...d.form, signedAt: todayInRome() } } : d
}

/**
 * TASK-0048: mirrors `PolicyNames.EqualStaff`'s `RequireRole(...)` set
 * (`EqualCrmPolicies.cs`) exactly — same technique as the `isAdmin(auth)` helper duplicated across
 * `CompanyDetailPage.tsx`/`FormDetailPage.tsx`/`ContestDetailPage.tsx`/`CustomerDetailPage.tsx`/
 * `Dashboard.tsx` for `PolicyNames.Admin`. `auth.role` is a 1:1 client-side echo of the same
 * `UserRole` enum the server-side policy checks (`AuthController.cs`: `role = user.Role.ToString()`),
 * so this is a faithful mirror, not a guess — unlike a per-category permission (e.g.
 * `CanManageForms`, see `FormDetailPage.tsx` / TASK-0036), which the frontend genuinely cannot know
 * in advance and must instead discover via a 403.
 */
function isEqualStaff(auth: LoginResponse): boolean {
  return [
    'Administrator',
    'EqualAdmin',
    'EqualDirettoreGenerale',
    'EqualDirettoreCommerciale',
    'EqualAreaManager',
    'EqualOperatore',
    'EqualBackOffice',
  ].includes(auth.role)
}

export function ContractFormPage() {
  const navigate = useNavigate()

  // TASK-0050: prefill `customerId` from the `?customerId=` query string, the way
  // `LeadDetailPage.tsx`'s "Crea Contratto" button links here for an already-converted lead
  // (`navigate('/contracts/create?customerId=' + lead.convertedCustomerId)`). Same
  // `useSearchParams().get(...)` pattern already used by `DocumentsPage.tsx` for its
  // entityType/entityId deep-link params — read once into the initial state, not synced back
  // afterward, so the field stays a normal editable input (not a locked value).
  const [searchParams] = useSearchParams()
  const [form, setForm] = useState<FormState>(() => {
    const customerId = searchParams.get('customerId')
    const base = { ...EMPTY_FORM, signedAt: todayInRome() }
    return customerId ? { ...base, customerId, customerMode: 'existing' } : base
  })
  const [saving, setSaving] = useState(false)

  // F-141 (REQ-041-001): bozza automatica in localStorage, dietro il flag. Spento: nessuna lettura ne' scrittura.
  const autosaveOn = useFeatureFlag('contratti.salvataggio_automatico')
  const [draftUserId, setDraftUserId] = useState<number | null>(null)
  const draftKey = draftUserId !== null ? `${DRAFT_KEY_PREFIX}${draftUserId}` : null
  const [offeredDraft, setOfferedDraft] = useState<StoredDraft | null>(null)
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null)
  const formRef = useRef(form)
  // F-148: invio in corso (azzerato solo su errore): il timer della bozza non deve riscrivere dopo la creazione.
  const submittingRef = useRef(false)
  formRef.current = form
  const initialSnapshot = useRef(JSON.stringify(form))
  const lastSnapshot = useRef('')
  // F-063: una chiave per tentativo di creazione; rigenerata dopo un successo.
  const idempotencyKey = useRef(newIdempotencyKey())
  const [error, setError] = useState<string | null>(null)

  // REQ-006-003: replaces the raw "ID Cliente" number input with a search picker.
  // `selectedCustomer` drives the display (name + code chip once picked); `form.customerId`
  // stays the single source of truth actually submitted, unchanged from before this task.
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerResults, setCustomerResults] = useState<Customer[]>([])
  const [customerNotice, setCustomerNotice] = useState<string | null>(null)
  // C-08: il cliente scelto ha dati mascherati per chi lo guarda: si offre "Mostra dati completi" (15 minuti, in audit).
  const [customerMasked, setCustomerMasked] = useState(false)
  // File del cliente già nel CRM (scheda cliente, pratica precedente): si vedono nei riquadri Documenti e si allegano al
  // salvataggio. `ownDoc[slot]` = l'utente preferisce caricare un file suo al posto di quello del cliente.
  const [customerDocs, setCustomerDocs] = useState<CustomerDocumentCandidate[]>([])
  const [ownDoc, setOwnDoc] = useState<Partial<Record<DocSlotKey, boolean>>>({})

  // REQ-006-003: resolve the `?customerId=` prefill (TASK-0050, above) to a display name, so a
  // lead-conversion deep link still shows who the contract is for instead of a bare ID. Best
  // effort only — if the id doesn't resolve (stale link, no access), `form.customerId` still
  // carries it through to submit, it just won't show a name chip.
  useEffect(() => {
    const idStr = searchParams.get('customerId')
    const id = idStr ? parseInt(idStr, 10) : NaN
    if (isNaN(id) || id <= 0) return
    customersApi
      .get(id)
      .then(c => {
        setSelectedCustomer(c)
        const { map, segment } = portalFromCustomer(c)
        fillPortal(map, segment)
      })
      .catch(() => {
        /* prefilled id doesn't resolve to a visible customer — keep the id, skip the chip */
      })
    void fillFromPreviousContracts(id)
    void loadCustomerDocs(id)
    // Only ever needs to run once, against the query string this page landed on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // REQ-006-003: debounced live search, same `customersApi.list({ search, size })` call
  // CustomersPage.tsx drives its own list from — 300ms mirrors a normal typeahead debounce and
  // avoids firing a request per keystroke while typing a name.
  useEffect(() => {
    const term = customerSearch.trim()
    if (!term) {
      setCustomerResults([])
      return
    }
    const handle = setTimeout(() => {
      customersApi
        .list({ search: term, size: 10 })
        .then(res => setCustomerResults(res.items))
        .catch(() => setCustomerResults([]))
    }, 300)
    return () => clearTimeout(handle)
  }, [customerSearch])

  function selectCustomer(c: Customer): void {
    setSelectedCustomer(c)
    setForm(f => ({ ...f, customerId: String(c.id), customerMode: 'existing' }))
    setCustomerSearch('')
    setCustomerResults([])
    setCustomerMasked(Object.values(c).some(v => typeof v === 'string' && isMasked(v)))
    prefillFromCustomer(c)
  }

  /** Scheda cliente (o riga dell'elenco) e pratiche precedenti: si rilancia anche dopo "Mostra dati completi". */
  function prefillFromCustomer(c: Customer): void {
    customersApi.get(c.id).then(d => {
      const { map, segment } = portalFromCustomer(d)
      fillPortal(map, segment)
    }).catch(() => {
      // Scheda non leggibile (cliente di un altro agente trovato per CF/P.IVA): si precompila con i dati dell'elenco.
      const { map, segment } = portalFromCustomer(c)
      fillPortal(map, segment)
    })
    void fillFromPreviousContracts(c.id)
    void loadCustomerDocs(c.id)
  }

  /** I file del cliente che la pratica riceverà al salvataggio: si mostrano subito nei riquadri Documenti. */
  async function loadCustomerDocs(customerId: number): Promise<void> {
    try {
      const r = await contractsApi.customerDocuments(customerId)
      setCustomerDocs(r.documenti)
      setOwnDoc({})
    } catch {
      setCustomerDocs([])
    }
  }

  /** Dalle pratiche precedenti del cliente: documento d'identità, nascita, pagamento e IBAN (solo i campi ancora vuoti). */
  async function fillFromPreviousContracts(customerId: number): Promise<void> {
    try {
      const r = await contractsApi.customerDefaults(customerId)
      // Un valore mascherato (IT60X054****1234, per chi non vede i dati interi) non si mette nel modulo.
      const data = Object.fromEntries(Object.entries(r.data).filter(([, v]) => v && !isMasked(v)))
      const masked = Object.values(r.data).some(isMasked)
      if (masked) setCustomerMasked(true)
      if (Object.keys(data).length === 0 && !masked) return
      fillPortal(data)
      setCustomerNotice([
        r.from ? `Dati del cliente (documento d'identità, pagamento, IBAN) presi dalla pratica ${r.from}: controllali.` : '',
        masked ? 'Alcuni dati (IBAN, codice fiscale, telefono) arrivano mascherati e non sono stati inseriti: con «Mostra dati completi» si compilano interi.' : '',
      ].filter(Boolean).join(' ') || null)
    } catch {
      // Nessuna pratica precedente leggibile: si compila a mano.
    }
  }

  function clearCustomer(): void {
    setSelectedCustomer(null)
    setCustomerNotice(null)
    setCustomerMasked(false)
    setCustomerDocs([])
    setOwnDoc({})
    setForm(f => ({ ...f, customerId: '' }))
  }

  // TASK-0048: this page has no `auth` prop today (App.tsx does not route one through — out of
  // this task's allowed_paths), so it fetches the current session itself, the same call
  // `App.tsx` already makes on mount (`fetchMe()`, `GET /api/auth/me`). `null` while loading is
  // treated as "not EqualStaff" (fail closed): the amount fields only appear once we positively
  // know the caller's tier, never optimistically.
  const [auth, setAuth] = useState<LoginResponse | null>(null)
  useEffect(() => {
    fetchMe().then(a => { setAuth(a); setDraftUserId(a?.id ?? null) }).catch(() => setAuth(null))
  }, [])
  const canSetAmounts = auth !== null && isEqualStaff(auth)
  // Agente di riferimento: lo staff lo sceglie, un account agente e' sempre se stesso (lo decide il server).
  const [agents, setAgents] = useState<DirectoryUser[]>([])
  useEffect(() => {
    if (!canSetAmounts) return
    userDirectoryApi.list({ roles: ['EqualAgenzia', 'EqualAgente', 'EqualSegnalatore'], activeOnly: true, limit: 200 })
      .then(setAgents).catch(() => setAgents([]))
  }, [canSetAmounts])

  // F-141: al rientro si propone il ripristino della bozza dell'utente (se ce n'e' una).
  useEffect(() => {
    if (!autosaveOn || !draftKey) return
    const draft = readDraft(draftKey)
    if (draft && JSON.stringify(draft.form) !== initialSnapshot.current) setOfferedDraft(draft)
  }, [autosaveOn, draftKey])

  // F-141: ogni 10 secondi, solo se il modulo e' cambiato rispetto all'ultimo salvataggio (e dall'inizio).
  // Finche' la proposta di ripristino e' aperta non si scrive, per non sovrascrivere la bozza da proporre.
  // C-06: scrittura della bozza, usata dal timer (ogni 10 secondi) e all'uscita da un campo.
  function saveDraftNow(): void {
    if (!autosaveOn || !draftKey || offeredDraft || submittingRef.current) return
    const snapshot = JSON.stringify(formRef.current)
    if (!shouldWriteDraft(snapshot, lastSnapshot.current, initialSnapshot.current)) return
    const savedAt = new Date().toISOString()
    // Storage pieno o bloccato: il modulo resta com'e', senza bozza.
    if (!writeDraft(draftKey, formRef.current, savedAt)) return
    lastSnapshot.current = snapshot
    setDraftSavedAt(savedAt)
  }
  const saveDraftRef = useRef(saveDraftNow)
  saveDraftRef.current = saveDraftNow

  useEffect(() => {
    if (!autosaveOn || !draftKey || offeredDraft) return
    const timer = setInterval(() => saveDraftRef.current(), DRAFT_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [autosaveOn, draftKey, offeredDraft])

  function restoreDraft(): void {
    if (!offeredDraft) return
    setForm(offeredDraft.form)
    lastSnapshot.current = JSON.stringify(offeredDraft.form)
    setDraftSavedAt(offeredDraft.savedAt)
    const id = parseInt(offeredDraft.form.customerId, 10)
    if (!isNaN(id) && id > 0) {
      customersApi.get(id).then(c => setSelectedCustomer(c)).catch(() => { /* resta l'id, senza etichetta */ })
    }
    setOfferedDraft(null)
  }

  function discardDraft(): void {
    removeDraft(draftKey)
    lastSnapshot.current = ''
    setDraftSavedAt(null)
    setOfferedDraft(null)
  }

  // REQ-006-005: wizard "Nuova pratica" step 3 ("Select Sector") — same one-shot
  // `fetchDepartments()` on mount that `ProductsPage.tsx` already drives its own "Reparto"
  // selector from.
  const [departments, setDepartments] = useState<Department[]>([])
  useEffect(() => {
    fetchDepartments()
      .then(setDepartments)
      .catch(() => setDepartments([]))
  }, [])

  // F-055 (TASK-0309): products on sale today, narrowed by department and segment. The operator is
  // never typed: the server derives it from the chosen product, shown here read-only.
  const [products, setProducts] = useState<Product[]>([])
  const [operators, setOperators] = useState<Operator[]>([])
  useEffect(() => {
    // Elenco in ordine alfabetico (il server li dà nell'ordine di inserimento).
    fetchOperators()
      .then(r => setOperators([...r.items].sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }))))
      .catch(() => setOperators([]))
  }, [])
  useEffect(() => {
    let cancelled = false
    if (!form.operatorId) { setProducts([]); return }
    // Prodotti in vendita oggi per l'operatore scelto (e per privato/azienda, se gia' scelto).
    fetchProducts({
      operatorId: Number(form.operatorId), availableOn: todayInRome(), pageSize: 200, isOption: false,
      ...(form.departmentId ? { departmentId: Number(form.departmentId) } : {}),
      ...(form.customerSegment ? { customerType: form.customerSegment === 'business' ? 'company' as const : 'person' as const } : {}),
    })
      .then(r => r.items)
      .then(items => {
        if (cancelled) return
        setProducts(items)
        // A previously chosen product that the new filters exclude must not stay selected.
        setForm(f => (f.productId && !items.some(p => String(p.id) === f.productId) ? { ...f, productId: '' } : f))
      })
      .catch(() => { if (!cancelled) setProducts([]) })
    return () => { cancelled = true }
  }, [form.operatorId, form.departmentId, form.customerSegment])
  const operatorProducts = products
  // Uno Mobile - porta un amico: presentatore opzionale (se vuoto, scelta automatica lato server).
  const isUnoMobile = UNO_MOBILE_OPERATOR_IDS.includes(Number(form.operatorId))
  const [referrerSearch, setReferrerSearch] = useState('')
  const [referrerResults, setReferrerResults] = useState<UnoMobileReferrer[]>([])
  const [referrer, setReferrer] = useState<UnoMobileReferrer | null>(null)
  useEffect(() => {
    if (!isUnoMobile || referrer) { setReferrerResults([]); return }
    let cancelled = false
    const t = setTimeout(() => {
      unoMobileApi.referrers(referrerSearch, 10).then(r => { if (!cancelled) setReferrerResults(r) }).catch(() => { if (!cancelled) setReferrerResults([]) })
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [isUnoMobile, referrerSearch, referrer])

  const portalConfig = operatorPortalConfig(operators.find(o => String(o.id) === form.operatorId)?.name, Number(form.operatorId) || null)
  // Solo i documenti che chiede il portale dell'operatore (per una SIM niente bolletta; per l'energia luce e gas).
  const docKinds = docSlotsFor(portalConfig)
  const tipoCliente = tipoClienteFromSegment(form.customerSegment)
  // Moduli firmati delle opzioni (IVA 10%, accise...): si caricano come allegati al salvataggio.
  const [moduleFiles, setModuleFiles] = useState<Record<string, File | null>>({})
  const selectedProduct = operatorProducts.find(p => String(p.id) === form.productId)

  // Documenti letti all'inizio (carta d'identita', bollette, visura): precompilano i dati del portale e diventano allegati.
  const readOn = useFeatureFlag('documenti.lettura_automatica')
  const [docs, setDocs] = useState<Record<DocSlotKey, DocSlot>>(
    { carta_identita: EMPTY_SLOT, bolletta: EMPTY_SLOT, bolletta_gas: EMPTY_SLOT, visura: EMPTY_SLOT })
  const docsBusy = Object.values(docs).some(d => d.busy)
  // Riquadri: quelli chiesti dal portale, più un documento già caricato prima di scegliere l'operatore.
  const shownDocKinds = DOC_SLOTS
    .map(d => docKinds.find(k => k.slot === d.slot) ?? (docs[d.slot].file ? d : null))
    .filter((d): d is DocSlotDef => d !== null)
  const patchDoc = (slot: DocSlotKey, p: Partial<DocSlot>) => setDocs(d => ({ ...d, [slot]: { ...d[slot], ...p } }))
  // Le due bollette (luce e gas) lette insieme: la luce prima, la gas completa quello che manca.
  const billFields = (cur: Record<DocSlotKey, DocSlot>) => mergeReads(cur.bolletta.fields, cur.bolletta_gas.fields)

  let portal: Record<string, string> = {}
  try { portal = JSON.parse(form.portalJson) as Record<string, string> } catch { /* bozza rovinata: si riparte vuoti */ }
  // Ermes: una bolletta (fattura, luce o gas) tra i documenti è obbligatoria salvo riattivazione/subentro.
  const missing = missingPortalFields(portal, portalConfig, tipoCliente, moduleFiles,
    portalConfig.ermes ? !!(docs.bolletta.file || docs.bolletta_gas.file) : undefined)
  let sims: SimLine[] = []
  try { sims = portalConfig.multiSim ? JSON.parse(form.simsJson || '[]') as SimLine[] : [] } catch { /* bozza rovinata */ }
  const setSims = (next: SimLine[]) => setForm(f => ({ ...f, simsJson: JSON.stringify(next) }))
  const productNameById = (id: string) => operatorProducts.find(p => String(p.id) === id)?.name
  function setPortal(key: string, value: string) {
    setForm(f => {
      let cur: Record<string, string> = {}
      try { cur = JSON.parse(f.portalJson) as Record<string, string> } catch { /* vuoto */ }
      return { ...f, portalJson: JSON.stringify({ ...cur, [key]: value }) }
    })
  }
  // Sky: cosa si attiva (TV, Sky Wifi, Sky Mobile...) decide quali dati della linea chiedere. Si propone dal nome del
  // prodotto scelto, e si riscrive quando il prodotto cambia; una bozza ripresa con la scelta già fatta la tiene.
  const prevProductRef = useRef<string | null>(null)
  useEffect(() => {
    const prev = prevProductRef.current
    prevProductRef.current = form.productId
    if (portalConfig.profile !== PROFILE_SKY) return
    const t = skyTipologiaDaProdotto(selectedProduct?.name)
    if (!t) return
    const cambiato = prev !== null && prev !== form.productId
    if (!cambiato && portal['SKY_TIPOLOGIA']) return
    setPortal('SKY_TIPOLOGIA', t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.productId, selectedProduct?.name, portalConfig.profile])

  function fillPortal(map: Record<string, string>, segmentHint = '') {
    setForm(f => {
      let cur: Record<string, string> = {}
      try { cur = JSON.parse(f.portalJson) as Record<string, string> } catch { /* vuoto */ }
      const next = { ...cur }
      // Un valore mascherato (C-08: IT60X054****1234) conta come vuoto: si sostituisce appena arriva quello intero.
      for (const [k, v] of Object.entries(map)) if (!next[k]?.trim() || isMasked(next[k])) next[k] = v
      const segment = f.customerSegment || segmentHint
      return { ...f, customerSegment: segment, portalJson: JSON.stringify(next) }
    })
  }
  /** Indirizzo del cliente letto dai documenti: sostituisce anche quello già compilato (dalla scheda cliente). */
  function setPortalAddress(fields: Record<string, string>) {
    setForm(f => {
      let cur: Record<string, string> = {}
      try { cur = JSON.parse(f.portalJson) as Record<string, string> } catch { /* vuoto */ }
      return { ...f, portalJson: JSON.stringify(applyClientAddress(cur, fields)) }
    })
  }

  async function chooseDoc({ slot, kind }: DocSlotDef, file: File | undefined) {
    if (!file) return
    patchDoc(slot, { file, fields: {}, read: null, error: null, busy: readOn })
    if (!readOn) return
    try {
      const r = await documentsApi.readFile(file, kind)
      const values: Record<string, string> = {}
      for (const f of r.fields) values[f.name] = f.value
      patchDoc(slot, { fields: values, read: { source: r.source, values }, busy: false })
      const read = { ...docs, [slot]: { ...docs[slot], fields: values } }
      // Pratica aziendale (segmento o visura): la carta d'identità è del legale rappresentante.
      const business = form.customerSegment === 'business' || kind === 'visura' || !!docs.visura.file
      fillPortal(portalFromDoc(kind, values, business),
        kind === 'visura' ? 'business' : kind === 'carta_identita' && !business ? 'consumer' : '')
      if (kind === 'visura' && Object.keys(docs.carta_identita.fields).length > 0)
        fillPortal(portalFromDoc('carta_identita', docs.carta_identita.fields, true))
      // L'indirizzo del cliente segue i documenti (visura, poi intestazione della bolletta, poi carta d'identità del
      // privato) anche sopra quello della scheda cliente (30/10/2026), qualunque sia l'ordine di lettura.
      const addr = clientAddressFromDocs(
        { carta_identita: read.carta_identita.fields, bolletta: billFields(read), visura: read.visura.fields }, business)
      if (addr) setPortalAddress(addr.fields)
      const holder = kind === 'visura' ? values['ragioneSociale']
        : kind === 'carta_identita' ? [values['nome'], values['cognome']].filter(Boolean).join(' ') : values['intestatario']
      if (form.customerId) return
      // Anti-duplicati: prima si cerca il cliente per CF/P.IVA letti dal documento, poi per nome.
      const id = docIdentity(values)
      const found = id ? await customersApi.list({ search: id, size: 10 }).then(res => res.items).catch(() => []) : []
      if (found.length === 1) {
        selectCustomer(found[0])
        setCustomerNotice(`Cliente già presente in anagrafica: ${displayName(found[0])} (${found[0].customerCode}). Selezionato in automatico.`)
      } else if (found.length > 1) {
        setCustomerSearch(id)
        setForm(f => ({ ...f, customerMode: 'existing' }))
        setCustomerNotice('Più clienti con lo stesso codice fiscale/P.IVA: scegli quello giusto invece di crearne uno nuovo.')
      } else if (holder && !customerSearch) setCustomerSearch(holder)
    } catch (err) {
      patchDoc(slot, { error: err instanceof Error ? err.message : String(err), busy: false })
    }
  }

  // F-074 (TASK-0328): campi dinamici applicabili a settore e prodotto scelti. I valori compilati restano in un
  // ref (nessun re-render a ogni tasto) e si salvano subito dopo la creazione della pratica.
  const dynamicValues = useRef<Record<string, FieldValue>>({})
  const fieldContext = {
    departmentId: form.departmentId ? Number(form.departmentId) : (selectedProduct?.departmentId ?? null),
    operatorId: form.operatorId ? Number(form.operatorId) : (selectedProduct?.operatorId ?? null),
    productId: form.productId ? Number(form.productId) : null,
    customerSegment: form.customerSegment || null,
    contractType: form.contractType || null,
  }

  function setField(
    key: keyof FormState,
  ): (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void {
    return e => setForm(f => ({ ...f, [key]: e.target.value }))
  }

  async function handleSave(): Promise<void> {
    if (!form.operatorId) {
      setError("Seleziona l'operatore.")
      return
    }
    if (!form.customerMode) {
      setError('Indica se il cliente è già in anagrafica o è nuovo.')
      return
    }
    if (form.customerMode === 'existing' && !(parseInt(form.customerId, 10) > 0)) {
      setError("Scegli il cliente dall'anagrafica (oppure segna che è nuovo).")
      return
    }
    if (form.customerMode === 'new' && !customerFromDocs(portal, { carta_identita: docs.carta_identita.fields, visura: docs.visura.fields })) {
      setError('Per creare il nuovo cliente servono almeno nome e cognome (o la ragione sociale) nei dati cliente della pratica.')
      return
    }
    if (!form.productId) {
      setError('Seleziona il prodotto della pratica.')
      return
    }
    const simProblems = portalConfig.multiSim ? simErrors(selectedProduct?.name, sims, productNameById) : []
    if (simProblems.length > 0) {
      setError(simProblems.join(' '))
      return
    }
    submittingRef.current = true
    setSaving(true)
    setError(null)
    try {
      const data: ContractCreateRequest = {}

      if (form.contractType) data.contractType = form.contractType
      // REQ-006-005: '' ("not yet classified") is never sent — same guarded-field convention as
      // contractType/customerSegment.
      if (form.departmentId) data.departmentId = Number(form.departmentId)
      if (canSetAmounts && form.agentId) data.agentId = Number(form.agentId)
      // REQ-006-004: '' ("not yet classified") is never sent — matches contractType's own
      // "only send if the caller actually picked something" convention right above.
      if (form.customerSegment) data.customerSegment = form.customerSegment as 'consumer' | 'business'
      if (form.notes) data.notes = form.notes
      // F-055: the operator is NOT sent - the server derives it from the product.
      data.productId = Number(form.productId)

      if (form.signedAt) data.signedAt = form.signedAt

      let fieldsWarning: string | null = null
      const addWarning = (m: string) => { fieldsWarning = fieldsWarning ? `${fieldsWarning} ${m}` : m }

      let custId = parseInt(form.customerId, 10)
      let custType = selectedCustomer?.customerType
      if (isNaN(custId) || custId <= 0) {
        // Nessun cliente scelto: si cerca per CF/P.IVA (dati cliente della pratica, poi documenti) e, se non c'è,
        // si crea dai documenti e dai dati cliente.
        const id = docIdentity({ partitaIva: portal['PARTITA_IVA_CLIENTE'], codiceFiscale: portal['CODICE_FISCALE_CLIENTE'] })
          || docIdentity({ ...billFields(docs), ...docs.carta_identita.fields, ...docs.visura.fields })
        const found = id ? await customersApi.list({ search: id, size: 10 }).then(r => r.items) : []
        if (found.length > 1) {
          setCustomerSearch(id)
          setForm(f => ({ ...f, customerMode: 'existing' }))
          setCustomerNotice('Più clienti con lo stesso codice fiscale/P.IVA: scegli quello giusto invece di crearne uno nuovo.')
          setError('Scegli il cliente prima di salvare.')
          submittingRef.current = false
          setSaving(false)
          return
        }
        if (found.length === 1) {
          custId = found[0].id
          custType = found[0].customerType
          selectCustomer(found[0])
        } else {
          const c = customerFromDocs(portal, { carta_identita: docs.carta_identita.fields, visura: docs.visura.fields })
          if (c) {
            try {
              const outcome = await createOrLinkCustomer((d, k) => customersApi.create(d, k), c, `${idempotencyKey.current}-cust`)
              if (outcome.kind === 'choose') {
                // Il server ha trovato più clienti con gli stessi dati (CF, P.IVA o email): si sceglie prima di salvare.
                const rows = await Promise.all(outcome.ids.map(cid => customersApi.get(cid).catch(() => null)))
                setCustomerResults(rows.filter((r): r is CustomerDetail => r !== null))
                setForm(f => ({ ...f, customerMode: 'existing' }))
                setCustomerNotice('Più clienti in anagrafica hanno gli stessi dati (CF, P.IVA o email): scegli quello giusto invece di crearne uno nuovo.')
                setError('Scegli il cliente prima di salvare.')
                submittingRef.current = false
                setSaving(false)
                return
              }
              custId = outcome.id
              if (outcome.kind === 'linked') {
                const existing = await customersApi.get(custId).catch(() => null)
                custType = existing?.customerType ?? c.customerType
                if (existing) setSelectedCustomer(existing)
                setForm(f => ({ ...f, customerId: String(custId), customerMode: 'existing' }))
                addWarning('Il cliente era già in anagrafica (stessi CF, P.IVA o email): collegato quello esistente.')
              } else {
                custType = c.customerType
                setForm(f => ({ ...f, customerId: String(custId) }))
                addWarning(outcome.forced
                  ? 'Cliente creato dai dati della pratica. Un contatto con gli stessi dati esisteva fuori dall’anagrafica clienti: controlla che non sia un doppione.'
                  : 'Cliente creato dai dati della pratica: controlla l’anagrafica.')
              }
            } catch (cErr) {
              addWarning(`Cliente non creato (${cErr instanceof Error ? cErr.message : String(cErr)}): collegalo dal dettaglio.`)
            }
          }
        }
      }
      if (custId > 0) data.customerId = custId

      // Azienda: l'anagrafica della pratica è il rappresentante legale. Se il cliente non lo ha ancora, lo si prende
      // dai documenti prima di creare la pratica, così il server collega subito il contatto.
      if (custId > 0 && (custType === 'company' || custType === 'condominium')) {
        const rep = legalRepFromDocs(portal, docs.carta_identita.fields)
        if (rep) {
          try {
            const existing = await customersApi.getLegalRepresentative(custId)
            if (!existing.representative) {
              await customersApi.putLegalRepresentative(custId, rep)
              addWarning('Rappresentante legale preso dai documenti: controllalo nella scheda cliente.')
            }
          } catch (rErr) {
            addWarning(`Rappresentante legale non salvato (${rErr instanceof Error ? rErr.message : String(rErr)}): inseriscilo nella scheda cliente.`)
          }
        }
      }

      // TASK-0048 / Bug #13: BaseAmount/AddonTotal are deliberately NOT part of the create
      // request — Contract.Create never accepts amounts from the caller, by design (server-side
      // recomputation only). They are set below, if at all, via a second, explicit call to the
      // existing PATCH /{id}/amounts endpoint.
      const createKey = idempotencyKey.current
      const res = await contractsApi.create(data, createKey)
      // Cliente già in anagrafica: i suoi documenti (carta d'identità, visura, dalla scheda cliente o dall'ultima
      // pratica) diventano allegati della nuova pratica, senza ricaricarli.
      if (custId > 0 && form.customerMode === 'existing') {
        try {
          // Per i tipi in cui l'utente ha caricato un file suo non si copia anche quello del cliente.
          const r = await contractsApi.copyCustomerDocuments(res.id, [...new Set(shownDocKinds.filter(k => docs[k.slot].file).map(k => k.kind))])
          if (r.copiati.length > 0) addWarning(`Presi dal cliente ${r.copiati.length} documenti: ${r.copiati.join(', ')}.`)
          else if (!r.fonti) addWarning(`Il cliente non ha documenti da prendere${r.dettaglio ? ` (${r.dettaglio})` : ''}: gli allegati vanno caricati a mano.`)
        } catch (dErr) {
          addWarning(`Documenti del cliente non presi (${dErr instanceof Error ? dErr.message : String(dErr)}): usa «Prendi i documenti del cliente» negli allegati.`)
        }
      }
      idempotencyKey.current = newIdempotencyKey()
      // F-141: pratica creata, la bozza non serve piu' (e non deve ricomparire).
      removeDraft(draftKey)

      // F-074: i campi dinamici si salvano sulla pratica appena creata. Se il salvataggio fallisce (es. un
      // formato non valido) la pratica esiste comunque: si apre il dettaglio dicendolo chiaramente.
      const portalData = Object.fromEntries(Object.entries({ ...withPortalDefaults(portal, portalConfig), TIPO_CLIENTE: tipoCliente }).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v))
      if (Object.keys(portalData).length > 0) {
        try { await contractsApi.setPortalData(res.id, portalData) } catch (pErr) {
          addWarning(`Contratto creato, ma i dati per il portale non sono stati salvati (${pErr instanceof Error ? pErr.message : String(pErr)}).`)
        }
      }
      // Ogni riquadro con un file (anche le due bollette, luce e gas) diventa un allegato; due file con lo stesso nome
      // prendono il prefisso del riquadro («luce-fattura.pdf», «gas-fattura.pdf»).
      const slotFiles = shownDocKinds.map(d => docs[d.slot].file)
      const uploadFile = (d: DocSlotDef) => {
        const f = docs[d.slot].file
        return f ? distinctUploadFile(f, d.prefix, slotFiles) : null
      }
      for (const def of shownDocKinds) {
        const { kind, label } = def
        const slot = docs[def.slot]
        const file = uploadFile(def)
        if (!file) continue
        let docId: number | null = null
        try {
          docId = (await documentsApi.upload('Contract', res.id, file, undefined, undefined, kind)).id
        } catch {
          addWarning(`Contratto creato, ma il documento "${label}" non è stato caricato. Caricalo dal dettaglio.`)
        }
        const confirmed = Object.fromEntries(Object.entries(slot.fields).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v))
        if (docId != null && slot.read && Object.keys(confirmed).length > 0) {
          // Insegna il modello del fornitore: il prossimo documento uguale si legge senza IA.
          try { await documentsApi.readConfirm(docId, kind, confirmed) } catch { /* non blocca la pratica */ }
        }
      }
      const pda = portalConfig.signedPda ? moduleFiles[SIGNED_PDA_KEY] : null
      if (pda) {
        try { await documentsApi.upload('Contract', res.id, pda, undefined, undefined, SIGNED_PDA_DOCUMENT_TYPE) } catch {
          addWarning('Contratto creato, ma la PDA firmata non è stata caricata: caricala dal dettaglio.')
        }
      }
      // SIM collegate: una pratica per SIM, stesso cliente e stessi allegati, collegata nei dati portale.
      const simIds: number[] = []
      for (const [i, sim] of sims.entries()) {
        try {
          const simRes = await contractsApi.create({ ...data, productId: Number(sim.productId) }, `${createKey}-sim-${i}`)
          simIds.push(simRes.id)
          await contractsApi.setPortalData(simRes.id, simPortalData(portalData, sim, res.id))
          if (pda) await documentsApi.upload('Contract', simRes.id, pda, undefined, undefined, SIGNED_PDA_DOCUMENT_TYPE)
          for (const def of shownDocKinds) {
            const f = uploadFile(def)
            if (f) await documentsApi.upload('Contract', simRes.id, f, undefined, undefined, def.kind)
          }
        } catch (sErr) {
          addWarning(`SIM ${i + 1}: pratica collegata non completata (${sErr instanceof Error ? sErr.message : String(sErr)}).`)
        }
      }
      if (simIds.length > 0) {
        try { await contractsApi.setPortalData(res.id, { ...portalData, [LINKED_PRACTICES_KEY]: simIds.join(',') }) } catch (lErr) {
          addWarning(`Le SIM sono state create (${simIds.join(', ')}) ma non collegate alla pratica (${lErr instanceof Error ? lErr.message : String(lErr)}).`)
        }
      }
      for (const m of portalConfig.modules) {
        const file = moduleFiles[m.flag]
        if (portal[m.flag] !== 'SI' || !file) continue
        try { await documentsApi.upload('Contract', res.id, file, undefined, undefined, `modulo_${m.flag.toLowerCase()}`) } catch {
          addWarning(`Contratto creato, ma il modulo "${m.label}" non è stato caricato. Caricalo dal dettaglio.`)
        }
      }
      // Punti di fornitura: dai dati del portale (gia' precompilati dalla bolletta e corretti dall'operatore).
      const points: { supplyType: 'pod' | 'pdr'; code: string; address: string | undefined }[] = []
      if (portalData['POD']) points.push({ supplyType: 'pod', code: portalData['POD'], address: fullAddress(portalData, 'POD') })
      if (portalData['PDR']) points.push({ supplyType: 'pdr', code: portalData['PDR'], address: fullAddress(portalData, 'PDR') })
      for (const sp of points) {
        try {
          await supplyPointsApi.create({ ...sp, contractId: res.id, customerId: custId > 0 ? custId : undefined })
        } catch (spErr) {
          addWarning(`Il punto di fornitura ${sp.supplyType.toUpperCase()} non è stato creato (${spErr instanceof Error ? spErr.message : String(spErr)}). Aggiungilo dal cliente.`)
        }
      }
      if (UNO_MOBILE_OPERATOR_IDS.includes(Number(form.operatorId))) {
        // Non blocca mai il salvataggio: l'esito si legge nell'avviso del dettaglio.
        try {
          const a = await unoMobileApi.assign({
            friendContractId: res.id,
            ...(custId > 0 ? { friendCustomerId: custId } : {}),
            ...(referrer ? { referrerCustomerId: referrer.customerId } : {}),
          })
          addWarning(`Codice amico Uno Mobile ${a.code} assegnato a ${a.referrerName}${a.auto ? ' (scelto in automatico)' : ''}${a.generated ? ', generato ora dal portale' : ''}.`)
        } catch (aErr) {
          if (aErr instanceof ApiError && aErr.status === 409) addWarning(`Codice amico Uno Mobile non assegnato: ${aErr.message || 'nessun codice disponibile'}.`)
          else addWarning(`Contratto creato, ma l'assegnazione del codice amico non è riuscita (${aErr instanceof Error ? aErr.message : String(aErr)}).`)
        }
      }
      if (Object.keys(dynamicValues.current).length > 0) {
        try {
          await contractFieldsApi.save(res.id, dynamicValues.current)
        } catch (fieldsErr) {
          const msg = fieldsErr instanceof Error ? fieldsErr.message : String(fieldsErr)
          const fieldsMsg = `Contratto creato, ma i dati aggiuntivi non sono stati salvati (${msg}). Completali dal dettaglio.`
          fieldsWarning = fieldsWarning ? `${fieldsWarning} ${fieldsMsg}` : fieldsMsg
        }
      }

      if (canSetAmounts) {
        const baseAmountNum = parseFloat(form.baseAmount)
        const addonTotalNum = parseFloat(form.addonTotal)
        const hasAmounts = !isNaN(baseAmountNum) || !isNaN(addonTotalNum)
        if (hasAmounts) {
          try {
            await contractsApi.updateAmounts(res.id, {
              baseAmount: isNaN(baseAmountNum) ? 0 : baseAmountNum,
              addonTotal: isNaN(addonTotalNum) ? 0 : addonTotalNum,
            })
          } catch (amountsErr) {
            // The contract itself WAS created successfully (res.id is real) — only the amounts
            // step failed (e.g. a 403 if the session's tier changed mid-flow, or a transient
            // error). Reported honestly rather than silently discarded or masked as full success:
            // navigate to the real contract, but say plainly that amounts were not saved.
            //
            // Independent review fix (TASK-0048): a local `setWarning(...)` call made HERE, right
            // after `navigate(...)`, would be a no-op the user never sees — `navigate` swaps the
            // route, React Router unmounts THIS component (ContractFormPage) as part of the same
            // update, and the local state holding the message is torn down with it before it can
            // ever render. Verified empirically (headless Chrome, mocked 403 on the amounts PATCH):
            // the page landed on the real contract with no warning shown anywhere in the DOM. The
            // message must instead travel WITH the navigation, via React Router's location `state`,
            // so the page that's actually still mounted afterwards (`ContractDetailPage`) can read
            // and render it.
            const msg = amountsErr instanceof Error ? amountsErr.message : String(amountsErr)
            navigate('/contracts/' + res.id, {
              state: {
                amountsWarning:
                  `Contratto creato, ma l'impostazione degli importi non è riuscita (${msg}). ` +
                  'Riprova più tardi.' + (fieldsWarning ? ` ${fieldsWarning}` : ''),
              },
            })
            removeDraft(draftKey)
            setSaving(false)
            return
          }
        }
      }

      removeDraft(draftKey)
      navigate('/contracts/' + res.id, fieldsWarning ? { state: { amountsWarning: fieldsWarning } } : undefined)
    } catch (err) {
      submittingRef.current = false
      setError(err instanceof Error ? err.message : 'Errore nel salvataggio')
      setSaving(false)
    }
  }

  // Cosa manca per salvare: lo dice la barra in fondo, al posto di un bottone spento senza spiegazione.
  const customerChosen = form.customerMode === 'new' || Number(form.customerId) > 0
  const toSave = [
    ...(!form.operatorId ? ["l'operatore"] : []),
    ...(!customerChosen ? ['il cliente'] : []),
    ...(!form.productId ? ['il prodotto'] : []),
  ]
  const step = (n: number, title: string, desc: string, done: boolean, children: ReactNode) => (
    <Card variant="outlined" padding="none" className="ecrm-step" data-done={done}>
      <div className="ecrm-step__head">
        <span className="ecrm-step__num" aria-hidden="true">{done ? <Icon name="check" size={18} /> : n}</span>
        <div>
          <h2 className="ecrm-step__title">{title}{isExplanation(desc) ? <HelpTip>{desc}</HelpTip> : null}</h2>
          {desc && !isExplanation(desc) ? <p className="ecrm-step__desc">{desc}</p> : null}
        </div>
      </div>
      {children}
    </Card>
  )

  return (
    <Page>
      <PageHeader title="Nuovo contratto" back={{ to: '/contracts', label: 'Contratti' }} />

      {error && <Callout tone="error">{error}</Callout>}

      {offeredDraft && (
        <Callout
          tone="info"
          role="status"
          action={
            <>
              <Button type="button" variant="tonal" size="sm" onClick={restoreDraft}>Ripristina</Button>
              <Button type="button" variant="text" size="sm" onClick={discardDraft}>Scarta</Button>
            </>
          }
        >
          Hai una bozza salvata alle {clockLabel(offeredDraft.savedAt)}. Vuoi ripristinarla?
        </Callout>
      )}

      <div onBlur={() => saveDraftRef.current()} className="ecrm-steps">

      {/* 1. Documenti, per primi: l'IA ne legge i dati del cliente, precompila il portale e cerca il cliente in anagrafica.
          Senza operatore si chiedono tutti; un file già caricato resta visibile anche se l'operatore poi non lo chiede. */}
      {step(1, 'Documenti del cliente', `Carica ${shownDocKinds.map(d => d.label.toLowerCase()).join(', ').replace(/, ([^,]*)$/, ' e $1')}: l'IA legge i dati del cliente, compila il resto del contratto e lo cerca in anagrafica.`
          + (shownDocKinds.some(d => d.slot === 'bolletta_gas') ? ' Luce e gas nella stessa bolletta: basta caricarla nel riquadro della luce.' : ''),
        shownDocKinds.some(({ slot }) => !!docs[slot].file), (
        <div className="d-flex flex-column gap-3">
            <div className="ecrm-filetiles">
              {shownDocKinds.map(def => {
                const { slot, kind, label } = def
                const d = docs[slot]
                // Cliente già in anagrafica: il suo file di questo tipo (carta d'identità, visura) riempie il riquadro e si
                // allega al salvataggio; «Carica altro» rimette il riquadro vuoto per un file diverso.
                const fromCustomer = slot === kind && !d.file && !ownDoc[slot] && form.customerMode === 'existing'
                  ? customerDocs.find(x => x.documentType === kind) : undefined
                if (fromCustomer) {
                  return (
                    <div key={slot} className="ecrm-filetile" title={`${fromCustomer.name} (${fromCustomer.origine})`}>
                      <Icon name="task" size={20} />
                      <strong>{label}</strong>
                      <span className="ecrm-filetile__name text-muted">{fromCustomer.name}</span>
                      <Button type="button" variant="text" size="sm" disabled={saving}
                        onClick={() => setOwnDoc(o => ({ ...o, [slot]: true }))}>Carica altro</Button>
                    </div>
                  )
                }
                return (
                  <FileTile key={slot} label={label} file={d.file} disabled={saving || d.busy}
                    {...(d.busy ? { status: 'Lettura…' } : d.read ? { status: d.read.source === 'modello' ? 'Letto (modello)' : "Letto con l'IA" } : {})}
                    onFile={f => void chooseDoc(def, f)} onClear={() => patchDoc(slot, EMPTY_SLOT)} />
                )
              })}
            </div>
            {form.customerMode === 'existing' && customerDocs.length > 0 && (
              <Callout tone="info" className="mt-2">
                {`Dal cliente, già nel CRM (${[...new Set(customerDocs.map(x => x.origine))].join(', ')}): `}
                {customerDocs.map(x => x.name).join(', ')}
                {'. Si allegano alla pratica al salvataggio; «Carica altro» per usare un file diverso.'}
              </Callout>
            )}
            {shownDocKinds.map(def => {
              const { slot, label } = def
              const d = docs[slot]
              if (!d.error && !(d.file && !d.busy && d.read)) return null
              return (
                <div key={slot}>
                  <div className="md-typescale-title-small mb-1">{label}</div>
                  {d.error && (
                    <Callout tone="warning" className="mt-2">
                      Non sono riuscito a leggere il documento ({d.error}). Scrivi i dati a mano: il file resta allegato.
                    </Callout>
                  )}
                  {d.file && !d.busy && d.read && (
                    <FormGrid className="mt-2">
                      {visibleFields(def, d.read.values).map(([name, fl]) => (
                        <div key={name}>
                          <TextField id={`ctf-${slot}-${name}`} label={fl} type="text" value={d.fields[name] ?? ''}
                            onChange={e => patchDoc(slot, { fields: { ...d.fields, [name]: e.target.value } })} />
                        </div>
                      ))}
                    </FormGrid>
                  )}
                </div>
              )
            })}
        </div>
      ))}


      {/* 2. Operatore e cliente: l'operatore decide prodotti e portale; il cliente esiste già o si crea al salvataggio. */}
      {step(2, 'Operatore e cliente', "Con chi carichi il contratto e per quale cliente.", !!form.operatorId && customerChosen && !!form.customerSegment, (
        <FormGrid>
          <div>
            <Select
              id="ctf-operatorId"
              label="Operatore *"
              required
              value={form.operatorId}
              onChange={e => {
                const operatorId = e.target.value
                setForm(f => ({
                  ...f,
                  operatorId,
                  productId: products.some(p => String(p.id) === f.productId && String(p.operatorId) === operatorId) ? f.productId : '',
                }))
              }}
            >
              <option value="">Seleziona un operatore…</option>
              {operators.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </Select>
          </div>
          <div>
            <span className="ecrm-choice-row__label">Il cliente è</span>
            <div className="ecrm-choice" role="radiogroup" aria-label="Tipo di cliente">
              {CUSTOMER_SEGMENT_OPTIONS.map(opt => (
                <label key={opt.value} htmlFor={`ctf-customerSegment-${opt.value}`}>
                  <input type="radio" id={`ctf-customerSegment-${opt.value}`} name="customerSegment" value={opt.value}
                    checked={form.customerSegment === opt.value} onChange={setField('customerSegment')} />
                  <Icon name={opt.value === 'business' ? 'business' : 'person'} size={18} />
                  {opt.value === 'business' ? 'Azienda' : 'Privato'}
                </label>
              ))}
            </div>
          </div>
          <div className="md-span-full">
            <span className="ecrm-choice-row__label">Cliente *</span>
            <div className="ecrm-choice" role="radiogroup" aria-label="Cliente">
              <label htmlFor="ctf-customerMode-existing">
                <input type="radio" id="ctf-customerMode-existing" name="customerMode" value="existing"
                  checked={form.customerMode === 'existing'} onChange={setField('customerMode')} />
                <Icon name="person_search" size={18} />Già in anagrafica
              </label>
              <label htmlFor="ctf-customerMode-new">
                <input type="radio" id="ctf-customerMode-new" name="customerMode" value="new"
                  checked={form.customerMode === 'new'}
                  onChange={e => { clearCustomer(); setForm(f => ({ ...f, customerMode: e.target.value })) }} />
                <Icon name="person_add" size={18} />Nuovo cliente
              </label>
            </div>
              {form.customerMode === 'existing' && (selectedCustomer ? (
                <div className="d-flex align-items-center gap-2 mt-2">
                  <TextField
                    id="ctf-customerSearch"
                    label="Cliente"
                    type="text"
                    readOnly
                    className="flex-grow-1"
                    value={`${displayName(selectedCustomer)} (${selectedCustomer.customerCode})`}
                  />
                  <Button
                    type="button"
                    variant="outlined"
                    size="sm"
                    className="flex-shrink-0"
                    onClick={clearCustomer}
                  >
                    Cambia
                  </Button>
                  {/* C-08: codice fiscale, telefono o IBAN mascherati per chi compila: visione motivata di 15 minuti su questo
                      cliente, poi i campi si riempiono interi. */}
                  {customerMasked && (
                    <RevealSensitiveButton entityType="customers" entityId={selectedCustomer.id}
                      onRevealed={() => prefillFromCustomer(selectedCustomer)} />
                  )}
                </div>
              ) : (
                <div className="mt-2">
                  <TextField
                    id="ctf-customerSearch"
                    label="Cerca cliente"
                    type="text"
                    supportingText="Per nome, ragione sociale, codice fiscale, P.IVA o codice cliente."
                    value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                    autoComplete="off"
                    autoFocus
                  />
                  {customerResults.length > 0 && (
                    <List aria-label="Clienti trovati" className="mt-1">
                      {customerResults.map(c => (
                        <ListItem
                          key={c.id}
                          headline={<>{displayName(c)}{' '}<span className="text-muted md-typescale-body-small">({c.customerCode})</span></>}
                          onClick={() => selectCustomer(c)}
                        />
                      ))}
                    </List>
                  )}
                  {customerSearch.trim() && customerResults.length === 0 && (
                    <div className="md-typescale-body-small text-muted mt-1">
                      Nessun cliente trovato: se non è in anagrafica segna "Nuovo cliente".
                    </div>
                  )}
                </div>
              ))}
              {form.customerMode === 'new' && (
                <div className="md-typescale-body-small text-muted mt-2">
                  L'anagrafica si crea al salvataggio dai documenti e dai dati cliente qui sotto (serve almeno il nome o la ragione sociale).
                  Se preferisci, <Link to="/customers/create" target="_blank" rel="noopener">creala prima in Anagrafica</Link> e poi scegli "Già in anagrafica".
                </div>
              )}
              {customerNotice && <Callout tone={selectedCustomer ? 'info' : 'warning'} className="mt-1">{customerNotice}</Callout>}
          </div>
        </FormGrid>
      ))}

      {/* 3. Offerta: prodotto in vendita oggi per operatore, reparto e tipo di cliente (F-055); importi solo per lo staff. */}
      {step(3, 'Offerta', 'Il prodotto venduto e, se servono, i campi specifici del prodotto.', !!form.productId, (
        <>
          <FormGrid>
            <div>
              <Select
                id="ctf-productId"
                label="Prodotto *"
                required
                value={form.productId}
                onChange={setField('productId')}
                disabled={!form.operatorId}
                supportingText={form.operatorId
                  ? `In vendita oggi${form.departmentId ? ' per il reparto scelto' : ''}.`
                  : "Scegli prima l'operatore (passo 1)."}
              >
                <option value="">Seleziona un prodotto…</option>
                {operatorProducts.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </div>
            {/* REQ-006-005: reparto facoltativo; senza resta "non ancora classificato". Filtra anche i prodotti. */}
            <div>
              <Select
                id="ctf-departmentId"
                label="Reparto"
                value={form.departmentId}
                onChange={setField('departmentId')}
              >
                <option value="">—</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Select>
            </div>
            {isUnoMobile && (
              <div>
                {referrer ? (
                  <div>
                    <span className="d-block md-typescale-body-small text-muted mb-1">Presentato da (porta un amico)</span>
                    {referrer.name} ({referrer.codesAvailable} codici disponibili){' '}
                    <Button type="button" variant="text" size="sm" onClick={() => { setReferrer(null); setReferrerSearch('') }}>Rimuovi</Button>
                  </div>
                ) : (
                  <>
                    <TextField id="ctf-referrer" label="Presentato da (porta un amico)" value={referrerSearch}
                      onChange={e => setReferrerSearch(e.target.value)}
                      supportingText="Facoltativo: cerca per nome. Se vuoto, il codice è scelto in automatico." />
                    {referrerResults.length > 0 && (
                      <List aria-label="Presentatori">
                        {referrerResults.map(r => (
                          <ListItem key={r.customerId} headline={r.name} supporting={`${r.codesAvailable} codici disponibili`}
                            onClick={() => setReferrer(r)} />
                        ))}
                      </List>
                    )}
                  </>
                )}
              </div>
            )}
          </FormGrid>
          {/*
            TASK-0048 / Bug #13: BaseAmount/AddonTotal are only ever shown to a caller the
            frontend already knows is EqualStaff-tier (`canSetAmounts`, mirroring
            `PolicyNames.EqualStaff` from `auth.role` — see `isEqualStaff` above), since only
            that tier can ever make the follow-up PATCH /{id}/amounts call succeed. A lower-tier
            caller (e.g. an Agent) never sees these fields at all, rather than being shown a
            control that would always 403 — same "hide, don't show-then-fail" precedent as the
            Admin-only merge panel on CompanyDetailPage.tsx.
          */}
          {canSetAmounts && (
            <FormGrid className="mt-3">
              <div>
                <TextField
                  id="ctf-baseAmount"
                  label="Importo Base (€)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.baseAmount}
                  onChange={setField('baseAmount')}
                />
              </div>
              <div>
                <TextField
                  id="ctf-addonTotal"
                  label="Addon Totale (€)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.addonTotal}
                  onChange={setField('addonTotal')}
                />
              </div>
            </FormGrid>
          )}

          {/* F-074: campi dinamici — compaiono quando settore e prodotto sono scelti. */}
          <div className="mt-3">
            <ContractDynamicFields
              context={fieldContext}
              onChange={values => {
                dynamicValues.current = values
              }}
            />
          </div>
        </>
      ))}

      {/* 4. Tutti i dati che il portale dell'operatore chiede per caricare il contratto. Con un cliente già scelto si
          può ripartire da un POD/PDR delle sue pratiche (anche di altri operatori). */}
      {step(4, 'Dati per il portale', missing.length > 0
        ? `Mancano ${missing.length} ${missing.length === 1 ? 'dato' : 'dati'} per il portale, anche dopo il salvataggio`
        : "Quello che il portale dell'operatore chiede per caricare il contratto.", !!form.operatorId && missing.length === 0, (
        <div>
          {portalConfig.kind !== 'telco' && Number(form.customerId) > 0 && (
            <FornitureNoteSelect customerId={Number(form.customerId)} disabled={saving}
              operatorName={id => operators.find(o => o.id === id)?.name}
              onApply={fields => setForm(f => {
                let cur: Record<string, string> = {}
                try { cur = JSON.parse(f.portalJson) as Record<string, string> } catch { /* vuoto */ }
                return { ...f, portalJson: JSON.stringify({ ...cur, ...fields }) }
              })} />
          )}
          <PraticaPortalSections values={portal} onChange={setPortal} disabled={saving} config={portalConfig}
            tipo={tipoCliente} moduleFiles={moduleFiles}
            onModuleFile={(flag, file) => setModuleFiles(m => ({ ...m, [flag]: file }))} />
          {portalConfig.multiSim && (
            <PraticaSimSection sims={sims} onChange={setSims} products={operatorProducts} disabled={saving} />
          )}
          {missing.length > 0 && (
            <Callout tone="info" className="mt-3">
              Per caricarlo sul portale mancano: {missing.join(', ')}. Puoi completarli anche dopo.
            </Callout>
          )}
        </div>
      ))}

      {/* 5. Firma, note e dettagli interni. */}
      {step(5, 'Firma e note', 'Data di firma, note e dettagli interni.', !!form.signedAt, (
        <FormGrid>
          <div>
            <TextField
              id="ctf-signed-at"
              label="Data di firma"
              type="date"
              value={form.signedAt}
              onChange={setField('signedAt')}
            />
          </div>
          <div>
            {canSetAmounts ? (
              <Select
                id="ctf-agentId"
                label="Agente di riferimento"
                value={form.agentId}
                onChange={setField('agentId')}
              >
                <option value="">—</option>
                {agents.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </Select>
            ) : auth !== null && (
              <TextField id="ctf-agentId" label="Agente di riferimento" type="text" readOnly value={auth.name || 'Tu'} />
            )}
          </div>
          <div>
            <TextField
              id="ctf-contractType"
              label="Tipo contratto"
              type="text"
              supportingText="Facoltativo, es. fibra, energia…"
              value={form.contractType}
              onChange={setField('contractType')}
            />
          </div>
          <div className="md-span-full">
            <TextField
              id="ctf-notes"
              label="Note"
              multiline
              rows={3}
              value={form.notes}
              onChange={setField('notes')}
            />
          </div>
        </FormGrid>
      ))}

      <div className="ecrm-savebar">
        <div className="ecrm-savebar__status" aria-live="polite">
          {error
            ? <span className="text-danger">{error}</span>
            : toSave.length === 3 && !Object.values(docs).some(d => d.file)
            ? <>Inizia dai <strong>documenti del cliente</strong>: l'IA compila il resto.</>
            : toSave.length > 0
            ? <>Per creare il contratto manca <strong>{toSave.join(', ').replace(/, ([^,]*)$/, ' e $1')}</strong>.</>
            : missing.length > 0
              ? <>Pronto da salvare. Per il portale mancano ancora {missing.length} {missing.length === 1 ? 'dato' : 'dati'}.</>
              : <><strong>Pronto da salvare.</strong></>}
          {autosaveOn && draftSavedAt && <> · Bozza salvata alle {clockLabel(draftSavedAt)}</>}
        </div>
        <ButtonLink to="/contracts" variant="text" onClick={() => removeDraft(draftKey)}>
          Annulla
        </ButtonLink>
        <Button
          type="button"
          variant="filled"
          icon={saving ? undefined : 'check'}
          loading={saving}
          disabled={saving || docsBusy || toSave.length > 0}
          onClick={() => void handleSave()}
        >
          {saving ? 'Salvataggio...' : 'Crea contratto'}
        </Button>
      </div>
      </div>
    </Page>
  )
}
